import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { VisibilitySelector } from '@/components/projects/visibility-selector'
import { useCreateProject } from '@/features/projects/use-projects'
import { createProjectSchema, type CreateProjectFormData } from '@/features/projects/schemas'
import { canonicalProjectUrl } from '@/lib/canonical-url'

export const ProjectNewPage = () => {
  const navigate = useNavigate()
  const createMutation = useCreateProject()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: { title: '', description: '', visibility: 'PUBLIC' },
  })

  const onSubmit = async (formData: CreateProjectFormData) => {
    try {
      const created = await createMutation.mutateAsync({
        title: formData.title,
        description: formData.description ? formData.description : null,
        visibility: formData.visibility,
      })
      toast.success('Project created')
      navigate(canonicalProjectUrl(created.author.handle, created.slug))
    } catch {
      toast.error('Failed to create project')
    }
  }

  const pending = isSubmitting || createMutation.isPending

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="gap-2 px-2">
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New project</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="What are you building?" {...register('title')} />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="A short summary of the build"
                rows={4}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            <Controller
              control={control}
              name="visibility"
              render={({ field }) => (
                <VisibilitySelector
                  value={field.value}
                  onChange={field.onChange}
                  name="new-visibility"
                  disabled={pending}
                />
              )}
            />
            {errors.visibility && (
              <p className="text-xs text-destructive">{errors.visibility.message}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? 'Creating…' : 'Create project'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
